import { FIXED_DT } from '../sim/motion';
import { idleInput, type TickInput } from '../sim/input';
import { uiHasFocus } from './ownership';

/**
 * Keyboard → TickInput with edge latching: a keydown+keyup inside one frame still yields exactly
 * one `pressed` tick. Never read `held` alone for jump. Gamepad/touch sources arrive later
 * (tablets are first-class — see the controller design report).
 *
 * Keyboard magnitude gets a SHORT ramp-in (~90 ms) so the locomotion blendspace eases through
 * walk→run instead of teleporting to full deflection — the analog stick does this naturally,
 * keys don't. Release is instant (a platformer's stop stays crisp; braking is the sim's job).
 *
 * INPUT OWNERSHIP (CLAUDE.md): while a UI surface owns the keyboard, this source yields the
 * NEUTRAL tick — the player stops walking behind the encounter card, and Arrow keys rove the
 * answer pills instead of simultaneously strafing the character. The zeroing happens HERE, at the
 * source: src/sim stays pure and the fixed-tick accumulator keeps running (it just receives idle
 * ticks), so determinism and replay are untouched.
 */
const RAMP_UP_S = 0.16;

/**
 * A Shift held for at most this many fixed ticks (~0.25 s at 60 Hz) counts as a TAP, which
 * toggles sprint. Anything longer is a hold, and sprint simply follows the key.
 */
const SPRINT_TAP_TICKS = 15;

export class KeyboardSource {
  private readonly down = new Set<string>();
  private pressedLatch = new Set<string>();
  private releasedLatch = new Set<string>();
  private rampedMag = 0;
  /**
   * TAP TO TOGGLE SPRINT, and it exists because of an OS-level fact rather than a design
   * preference: on macOS, Shift+Space is an input-method shortcut, so the OS eats the Space and
   * the browser is never given a keydown for it. Confirmed live - a capture-phase window listener
   * logged ShiftLeft and KeyW while a player jumped, and no Space event at all, on a build where
   * W+Space jumps perfectly. Nothing in this file can intercept an event it is never handed.
   *
   * Hold-to-sprint therefore CANNOT work on such a machine, because holding Shift is exactly what
   * breaks the jump. Toggling means the player is not holding Shift while they press Space, so
   * the combination never occurs. Hold still works for anyone whose OS leaves it alone.
   */
  private sprintToggle = false;
  /** Ticks the current Shift press has lasted, for telling a tap from a hold. */
  private sprintHeldTicks = 0;

  attach(target: Window): () => void {
    const onDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      this.down.add(e.code);
      this.pressedLatch.add(e.code);
      // Space scrolls the page — but it also ACTIVATES a focused button, so the preventDefault is
      // gated by ownership like every other global key. (Before the seam this preventDefault was
      // the only thing stopping Space from double-firing the encounter card's Continue.)
      if (e.code === 'Space' && !uiHasFocus()) e.preventDefault();
    };
    const onUp = (e: KeyboardEvent) => {
      this.down.delete(e.code);
      this.releasedLatch.add(e.code);
    };
    const onBlur = () => {
      for (const code of this.down) this.releasedLatch.add(code);
      this.down.clear();
      // Alt-tabbing away mid-sprint and coming back to a character still sprinting reads as a
      // stuck key, so the toggle goes with the keys it was standing in for.
      this.sprintToggle = false;
      this.sprintHeldTicks = 0;
    };
    target.addEventListener('keydown', onDown);
    target.addEventListener('keyup', onUp);
    target.addEventListener('blur', onBlur);
    return () => {
      target.removeEventListener('keydown', onDown);
      target.removeEventListener('keyup', onUp);
      target.removeEventListener('blur', onBlur);
    };
  }

  /** Build one tick's input and clear the edge latches. `cameraYaw` rotates move into world space. */
  consumeTick(cameraYaw: number): TickInput {
    // The UI owns the keyboard ⇒ neutral tick. Latches are still DRAINED (not preserved) so a
    // Space pressed to click a pill can never fire a jump the moment the card closes; `down` is
    // kept, so a key held across the card resumes correctly.
    if (uiHasFocus()) {
      this.pressedLatch = new Set();
      this.releasedLatch = new Set();
      this.rampedMag = 0;
      return idleInput();
    }
    const axis = (neg: string[], pos: string[]): number => {
      const n = neg.some((c) => this.down.has(c)) ? 1 : 0;
      const p = pos.some((c) => this.down.has(c)) ? 1 : 0;
      return p - n;
    };
    // Screen-space intent: x = right, y = forward (away from camera).
    let ix = axis(['KeyA', 'ArrowLeft'], ['KeyD', 'ArrowRight']);
    let iy = axis(['KeyS', 'ArrowDown'], ['KeyW', 'ArrowUp']);
    const mag = Math.hypot(ix, iy);
    if (mag > 1) {
      ix /= mag;
      iy /= mag;
    }
    // Ramp the magnitude (direction stays instant — only the "how hard" eases in).
    this.rampedMag = mag > 0 ? Math.min(1, this.rampedMag + FIXED_DT / RAMP_UP_S) : 0;

    // Rotate into world XZ. Convention: move.x = world X, move.y = world Z. At yaw 0 the camera
    // sits at +Z looking toward -Z, so screen-forward (W) is world -Z.
    const cos = Math.cos(cameraYaw);
    const sin = Math.sin(cameraYaw);
    const wx = (ix * cos - iy * sin) * this.rampedMag;
    const wz = (-ix * sin - iy * cos) * this.rampedMag;

    const rollKeys = ['ControlLeft', 'KeyC'];
    // Either Shift, because which one is under the hand depends on which side of the keyboard
    // the movement keys are on: WASD sits over the left Shift, the arrow keys over the right.
    const sprintKeys = ['ShiftLeft', 'ShiftRight'];

    // TAP toggles, HOLD follows the key. The tap is resolved on RELEASE, because that is the
    // first moment the two are distinguishable - a tap and the first tick of a hold are the same
    // event. Sprinting while the key is down regardless means a hold never waits on that.
    const sprintDown = sprintKeys.some((k) => this.down.has(k));
    if (sprintDown) {
      this.sprintHeldTicks += 1;
    }
    if (sprintKeys.some((k) => this.releasedLatch.has(k))) {
      if (this.sprintHeldTicks > 0 && this.sprintHeldTicks <= SPRINT_TAP_TICKS) {
        this.sprintToggle = !this.sprintToggle;
      }
      this.sprintHeldTicks = 0;
    }
    const sprinting = sprintDown || this.sprintToggle;
    const input: TickInput = {
      move: { x: wx, y: wz },
      jump: {
        held: this.down.has('Space'),
        pressed: this.pressedLatch.has('Space'),
        released: this.releasedLatch.has('Space'),
      },
      roll: {
        held: rollKeys.some((k) => this.down.has(k)),
        pressed: rollKeys.some((k) => this.pressedLatch.has(k)),
        released: rollKeys.some((k) => this.releasedLatch.has(k)),
      },
      // No preventDefault and no ownership branch of its own: Shift is a MODIFIER, it scrolls
      // nothing, and it is read only through this method - which already returns the neutral tick
      // while a UI surface owns the keyboard (hard rule 9). Shift+Tab therefore still reaches the
      // browser's focus traversal untouched.
      sprint: {
        held: sprinting,
        pressed: sprintKeys.some((k) => this.pressedLatch.has(k)),
        released: sprintKeys.some((k) => this.releasedLatch.has(k)),
      },
    };
    this.pressedLatch = new Set();
    this.releasedLatch = new Set();
    return input;
  }
}
